import { FluzElementsError } from "./errors.js";
import { resolveFrameHostOrigin } from "./frameHostOrigin.js";
import { mountFrame } from "./frameMount.js";
import type { FrameMount } from "./frameMount.js";
import type { StyleAllowlist } from "./style.js";

export type SecureInputField = "pan" | "expiry" | "cvv";

export interface SecureInputsOptions {
  clientToken: string;
  loadToken: string;
  frameHostOrigin?: string;
  mountId?: string;
  style?: StyleAllowlist;
  mountTimeoutMs?: number;
  submitTimeoutMs?: number;
  excludedCardBrands?: string[];
}

export interface FieldState {
  isEmpty: boolean;
  isValid: boolean;
  isDirty: boolean;
  brand?: string;
}

export interface BillingAddress {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  zipCode: string;
  country: string;
}

export interface SubmitOptions {
  cardholderName: string;
  billingAddress: BillingAddress | { userAddressId: string };
  isBackupPayment?: boolean;
}

export interface SubmitSuccess {
  bankCardId: string;
  brand: string;
  last4: string;
  expirationMonth: string;
  expirationYear: string;
  cardholderName: string;
  billingAddress: Record<string, unknown>;
  createdAt: string;
}

export interface DeclineInfo {
  code: string;
  message: string;
}

export interface SecureInputsController {
  mount(container: HTMLElement): Promise<void>;
  onChange(handler: (field: SecureInputField, state: FieldState) => void): () => void;
  onError(handler: (error: FluzElementsError) => void): () => void;
  onDeclined(handler: (decline: DeclineInfo) => void): () => void;
  onSuccess(handler: (result: SubmitSuccess) => void): () => void;
  submit(options: SubmitOptions): Promise<void>;
  destroy(): void;
}

const DEFAULT_SUBMIT_TIMEOUT_MS = 15000;

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// The frame's one submit-result message carries the full outcome directly --
// either a protocol-level failure (validation/collect-unavailable/network),
// or the actual payment-method response relayed byte-for-byte (the frame
// posted straight through VGS to frame-host, see
// apps/frame-host/src/frame/renderInputFrameDocument.ts -- the SDK makes no
// network call of its own for submit anymore).
interface SubmitAck {
  ok: boolean;
  code?: string;
  result?: { status: "success" | "declined" | "error"; [key: string]: unknown };
}

export function renderFieldsForTokenization(options: SecureInputsOptions): SecureInputsController {
  const frameHostOrigin = resolveFrameHostOrigin(options.frameHostOrigin);
  const mountId = options.mountId ?? generateId();

  const changeHandlers = new Set<(field: SecureInputField, state: FieldState) => void>();
  const errorHandlers = new Set<(error: FluzElementsError) => void>();
  const declinedHandlers = new Set<(decline: DeclineInfo) => void>();
  const successHandlers = new Set<(result: SubmitSuccess) => void>();
  let mount: FrameMount | null = null;
  let pendingSubmitResolver: ((ack: SubmitAck) => void) | null = null;
  let mounted = false;
  let submitInFlight = false;

  function emitError(error: FluzElementsError) {
    errorHandlers.forEach((handler) => handler(error));
  }

  return {
    async mount(container: HTMLElement) {
      if (mounted) {
        throw new FluzElementsError("MOUNT_FAILED", "SecureInputs is already mounted.");
      }
      mounted = true;

      const url = new URL("/v1/frame/card", frameHostOrigin);
      // See the matching comment in cardViewer.ts's mount(): loadToken
      // (short-lived, load-only) travels in the query string for
      // frame-host's grant lookup and CSP frame-ancestors header; the
      // collect-capable clientToken never touches the URL -- it's handed
      // over via the handshake postMessage below instead.
      url.searchParams.set("token", options.loadToken);

      try {
        mount = await mountFrame({
          mountId,
          frameUrl: url.toString(),
          frameOrigin: frameHostOrigin,
          container,
          style: options.style,
          mountTimeoutMs: options.mountTimeoutMs,
          onError: emitError,
          handshakeExtra: {
            clientToken: options.clientToken,
            excludedCardBrands: options.excludedCardBrands ?? [],
          },
          onMessage: (message) => {
            if (message.type === "field-change") {
              const payload = message.payload as { field: SecureInputField; state: FieldState };
              changeHandlers.forEach((handler) => handler(payload.field, payload.state));
              return;
            }
            if (message.type === "submit-result") {
              pendingSubmitResolver?.(message.payload as SubmitAck);
            }
          },
        });
      } catch (error) {
        mounted = false;
        throw error instanceof FluzElementsError ? error : new FluzElementsError("MOUNT_FAILED", "Failed to mount secure inputs.");
      }
    },

    onChange(handler) {
      changeHandlers.add(handler);
      return () => changeHandlers.delete(handler);
    },

    onError(handler) {
      errorHandlers.add(handler);
      return () => errorHandlers.delete(handler);
    },

    onDeclined(handler) {
      declinedHandlers.add(handler);
      return () => declinedHandlers.delete(handler);
    },

    onSuccess(handler) {
      successHandlers.add(handler);
      return () => successHandlers.delete(handler);
    },

    async submit(submitOptions: SubmitOptions) {
      if (!mounted || !mount) {
        throw new FluzElementsError("MOUNT_FAILED", "SecureInputs is not mounted.");
      }
      if (submitInFlight) {
        throw new FluzElementsError("SUBMIT_FAILED", "A submit() call is already in progress.");
      }
      submitInFlight = true;

      try {
        const timeoutMs = options.submitTimeoutMs ?? DEFAULT_SUBMIT_TIMEOUT_MS;
        const ackPromise = new Promise<SubmitAck>((resolve) => {
          pendingSubmitResolver = resolve;
        });
        mount.send("submit", submitOptions);

        const ackTimeout: { id?: ReturnType<typeof setTimeout> } = {};
        const ack = await Promise.race([
          ackPromise,
          new Promise<"timeout">((resolve) => {
            ackTimeout.id = setTimeout(() => resolve("timeout"), timeoutMs);
          }),
        ]);
        clearTimeout(ackTimeout.id);
        pendingSubmitResolver = null;

        if (ack === "timeout") {
          emitError(new FluzElementsError("SUBMIT_TIMEOUT", "Timed out waiting for a submit result."));
          return;
        }

        if (!ack.ok || !ack.result) {
          emitError(new FluzElementsError("SUBMIT_FAILED", ack.code ?? "submit_failed"));
          return;
        }

        if (ack.result.status === "success") {
          successHandlers.forEach((handler) => handler(ack.result as unknown as SubmitSuccess));
          return;
        }
        if (ack.result.status === "declined") {
          declinedHandlers.forEach((handler) =>
            handler({ code: ack.result?.code as string, message: ack.result?.message as string }),
          );
          return;
        }
        emitError(new FluzElementsError("SUBMIT_FAILED", (ack.result.code as string) ?? "submit_failed"));
      } finally {
        submitInFlight = false;
      }
    },

    destroy() {
      mount?.destroy();
      mount = null;
      mounted = false;
    },
  };
}
